import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { regex } from '@/utils/helper';
import {
  AddEditAddressUserDetails,
  AddressUserDetails,
} from '@/models/user-details';
import { useUser } from '@/context/userContext';
import { addressApi, getRegions } from '@/utils/helper';
import { State } from '@/models/lookup';
import Heading from '../Heading';
import { CheckBox, MobileNumberField, TextField } from '../Inputs';
import DropDownList from '../Inputs/DropDownList';
import Button from '../Button';
import { useToggle } from '@/hooks';
import { useLoader } from '@/context/loaderContext';
import { ALLOWED_REGIONS } from '@/constants';
import { storage } from '@/utils/storage';
import AddressField from '@/features/Cart/components/AddressField';
import { useLocation } from '@/context/locationContext';

type AddressPropTypes = {
  address?: AddressUserDetails;
  onClose?: () => void;
};

const AddressFormSchema = yup.object().shape({
  id: yup.number().optional(),
  firstname: yup
    .string()
    .trim()
    .required('First Name is Required')
    .matches(
      /^[A-Za-z]+$/,
      'Special characters and numbers are not allowed in the First Name field.'
    ),
  lastname: yup
    .string()
    .trim()
    .required('Last Name is Required')
    .matches(
      /^[A-Za-z]+$/,
      'Special characters and numbers are not allowed in the Last Name field.'
    ),
  mobile: yup
    .string()
    .trim()
    .required('Mobile Number is Required')
    .min(10, 'Invalid Mobile Number')
    .max(10, 'Invalid Mobile Number')
    .matches(/^[0-9]+$/, 'Only Numbers are allowed'),
  street1: yup.string().trim().required('Address is Required'),
  street2: yup.string().trim().required('Please enter the full address'),
  regionid: yup.number().required('State is Required'),
  city: yup.string().trim().required('City is Required'),
  pincode: yup
    .string()
    .trim()
    .required('Pincode is Required')
    .matches(regex.pincode, 'Invalid Pincode'),
  default_shipping: yup.boolean(),
});

const CustomerAddressForm: FC<AddressPropTypes> = ({ address, onClose }) => {
  const { setAddressString } = useLocation();
  const { userDetails, updateUserDetails } = useUser();
  const { setLoading } = useLoader();
  const { value: isSubmitting, toggleValue: toggleIsSubmitting } =
    useToggle(false);

  const [availableRegions, setAvailableRegions] = useState<State[]>([]);
  const [isLocationServiceable, setIsLocationServiceable] =
    useState<boolean>(true);
  const [defaultCity, setDefaultCity] = useState('');
  const [defaultState, setDefaultState] = useState('');
  const { city, state } = useLocation();
  const [mobileValidationError, setMobileValidationError] = useState<
    string | null
  >(null);

  useEffect(() => {
    const cityStored = storage.get('customer_city');
    const stateStored = storage.get('customer_state');
    setDefaultCity(cityStored || '');
    setDefaultState(stateStored || '');
  }, [city, state]);

  const defaultValues: AddEditAddressUserDetails | any = {
    id: address?.id,
    firstname: address ? address?.firstname : '',
    lastname: address ? address?.lastname : '',
    mobile: address ? address?.telephone : '',
    street1: address ? address?.street[0] : '',
    street2: address ? address?.street?.[1] : '',
    regionid: address ? Number(address?.region_id) : 578,
    // state: address ? address?.state : '',
    city: address ? address?.city : '',
    pincode: address
      ? (address as any)?.postcode || ''
      : storage.get('pincode') || '',
    default_billing:
      userDetails && userDetails?.default_billing === String(address?.id)
        ? 1
        : 0,
    default_shipping:
      userDetails && userDetails?.default_shipping === String(address?.id)
        ? 1
        : 0,
    latitude: address
      ? address?.custom_attributes?.find(
          item => item.attribute_code === 'latitude'
        )?.value
      : storage.get('customer_lat') || '',
    longitude: address
      ? address?.custom_attributes?.find(
          item => item.attribute_code === 'longitude'
        )?.value
      : storage.get('customer_lng') || '',
  };

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver<any>(AddressFormSchema),
    defaultValues,
  });

  const [selectedRegion, setSelectedRegion] = useState<State | undefined>(
    availableRegions?.find(
      item => item && item.id === String(defaultValues.regionid)
    )
  );

  useEffect(() => {
    async function getRegionsData() {
      setLoading(true);
      const regions = await getRegions();
      const filteredRegions = regions.filter((region: State) =>
        ALLOWED_REGIONS.includes(region.code)
      );
      setAvailableRegions(filteredRegions);

      // Set initial selected region
      const initialRegion = filteredRegions.find(
        (item: { id: string }) =>
          item && item.id === String(defaultValues.regionid)
      );
      setSelectedRegion(initialRegion);

      setLoading(false);
    }
    getRegionsData();
  }, [defaultValues.regionid]);

  // Update the useEffect for initialization
  useEffect(() => {
    if (address) {
      // Edit mode - use saved address
      setValue('street1', address.street[0]);
      setValue('city', address.city);
      setValue('regionid', Number(address.region_id));

      // Store for AddressField
      storage.set('customer_addressString', address.street[0]);
      setAddressString(address.street[0]);
      storage.set('customer_city', address.city);
      storage.set('customer_state', address.region.region);

      // Set coordinates if available
      const latitude = address.custom_attributes?.find(
        item => item.attribute_code === 'latitude'
      )?.value;
      const longitude = address.custom_attributes?.find(
        item => item.attribute_code === 'longitude'
      )?.value;

      if (latitude) storage.set('customer_lat', latitude);
      if (longitude) storage.set('customer_lng', longitude);

      // Force AddressField to show as serviceable for existing addresses
      storage.set('customer_serviceable', 'true');
    }
  }, [address, setValue]);

  // Add useEffect to set default city and state when adding new address
  useEffect(() => {
    // Only set defaults for new address (when address prop is not provided)
    if (!address && defaultCity) {
      setValue('city', defaultCity);
      trigger('city');
    }

    // For state, check if defaultState matches any available region
    if (!address && defaultState && availableRegions?.length > 0) {
      const matchedRegion = availableRegions.find(region => {
        const regionName = region.name.toLowerCase();
        const defaultStateClean = defaultState
          .toLowerCase()
          .replace(/\s*state\s*/i, '')
          .trim();

        return (
          regionName === defaultStateClean ||
          // Handle special cases
          (regionName === 'delhi' && defaultStateClean.includes('new delhi')) ||
          (regionName === 'telangana' &&
            defaultStateClean.includes('hyderabad')) ||
          (regionName === 'uttar pradesh' && defaultStateClean.includes('up'))
        );
      });

      if (matchedRegion) {
        setSelectedRegion(matchedRegion);
        setValue('regionid', Number(matchedRegion.id));
        trigger('regionid');
      }
    }
  }, [address, defaultCity, defaultState, availableRegions, setValue, trigger]);

  // Add initial serviceability check for default address
  useEffect(() => {
    // Check if we have default values
    if (!address && defaultCity && defaultState) {
      // If we have default values, check if the current stored address is serviceable
      const serviceable = storage.get('customer_serviceable');
      setIsLocationServiceable(serviceable === 'true');
    }
  }, [address, defaultCity, defaultState]);

  // Add this effect to initialize form values for default address
  useEffect(() => {
    // Only for new address with default values
    if (!address && defaultCity && defaultState) {
      // Set initial form values
      setValue('firstname', '');
      setValue('lastname', '');
      setValue('mobile', '');
      setValue('street1', storage.get('customer_addressString') || '');
      setValue('city', defaultCity, { shouldValidate: true });

      // Check serviceability
      const serviceable = storage.get('customer_serviceable');
      setIsLocationServiceable(serviceable === 'true');

      // Set state if available
      if (availableRegions?.length > 0) {
        const matchedRegion = availableRegions.find(region => {
          const regionName = region.name.toLowerCase();
          const defaultStateClean = defaultState
            .toLowerCase()
            .replace(/\s*state\s*/i, '')
            .trim();

          return (
            regionName === defaultStateClean ||
            (regionName === 'delhi' &&
              defaultStateClean.includes('new delhi')) ||
            (regionName === 'telangana' &&
              defaultStateClean.includes('hyderabad')) ||
            (regionName === 'uttar pradesh' && defaultStateClean.includes('up'))
          );
        });

        if (matchedRegion) {
          setSelectedRegion(matchedRegion);
          setValue('regionid', Number(matchedRegion.id), {
            shouldValidate: true,
          });
        }
      }
    }
  }, [address, defaultCity, defaultState, availableRegions, setValue]);

  // Add mobile number validation effect
  const mobileNumber = watch('mobile');
  useEffect(() => {
    if (mobileNumber) {
      if (!/^\d+$/.test(mobileNumber)) {
        setMobileValidationError('Only numbers are allowed');
      } else if (mobileNumber.length > 10) {
        setMobileValidationError('Mobile number cannot exceed 10 digits');
      } else if (mobileNumber.length === 10 && !/^[6-9]/.test(mobileNumber)) {
        setMobileValidationError('Mobile number must start with 6, 7, 8, or 9');
      } else if (mobileNumber.length < 10 && mobileNumber.length > 0) {
        setMobileValidationError('Mobile number must be 10 digits');
      } else {
        setMobileValidationError(null);
      }
    } else {
      setMobileValidationError(null);
    }
  }, [mobileNumber]);

  const onSubmit = handleSubmit(async values => {
    toggleIsSubmitting(true);
    const address: AddEditAddressUserDetails = {
      firstname: values.firstname,
      lastname: values.lastname,
      mobile: values.mobile,
      street1: values.street1,
      street2: values?.street2,
      city: values?.city,
      regionid: values?.regionid,
      // state: values?.state,
      id: undefined,
      default_billing: values?.default_shipping ? 1 : 0,
      default_shipping: values?.default_shipping ? 1 : 0,
      latitude: storage.get('customer_lat') || '',
      longitude: storage.get('customer_lng') || '',
    };
    // Attach pincode for backend while keeping existing type usage
    (address as any).pincode = values?.pincode;
    if (!values?.id) {
      const res = await addressApi({
        type: 'add',
        addressData: address,
        userDetails,
      });
      if (res?.success) {
        toast.success('New Address Added Successfully!');
        updateUserDetails();
      } else {
        toast.error(res?.error);
      }
    } else {
      const res = await addressApi({
        type: 'update',
        addressData: address,
        userDetails,
        idToUpdateDelete: String(values.id),
      });
      if (res?.success) {
        toast.success('Updated Address Successfully!');
        updateUserDetails();
      } else {
        toast.error(res?.error);
      }
    }
    toggleIsSubmitting(false);
    if (onClose) {
      onClose();
    }
  });

  // Initialize form with default values properly
  useEffect(() => {
    if (!address) {
      if (defaultCity) {
        setValue('city', defaultCity, { shouldValidate: true });
      }
      if (defaultState && availableRegions?.length > 0) {
        const matchedRegion = availableRegions.find(region => {
          const regionName = region.name.toLowerCase();
          const defaultStateClean = defaultState
            .toLowerCase()
            .replace(/\s*state\s*/i, '')
            .trim();

          return (
            regionName === defaultStateClean ||
            (regionName === 'delhi' &&
              defaultStateClean.includes('new delhi')) ||
            (regionName === 'telangana' &&
              defaultStateClean.includes('hyderabad')) ||
            (regionName === 'uttar pradesh' && defaultStateClean.includes('up'))
          );
        });

        if (matchedRegion) {
          setSelectedRegion(matchedRegion);
          setValue('regionid', Number(matchedRegion.id), {
            shouldValidate: true,
          });
        }
      }
    }
  }, [address, defaultCity, defaultState, availableRegions, setValue]);

  return (
    <>
      <form
        name='address-form'
        className='flex flex-col gap-y-4'
        onSubmit={onSubmit}>
        <div aria-label='personal-details'>
          <Heading className='px-0 text-left text-sm' as='h4'>
            Personal Details
          </Heading>
          <div className='mt-4 flex gap-3'>
            <div className='flex-1'>
              <TextField
                id='firstname'
                placeholder='First Name*'
                register={register}
                error={errors?.firstname}
              />
            </div>
            <div className='flex-1'>
              <TextField
                id='lastname'
                placeholder='Last Name*'
                register={register}
                error={errors?.lastname}
              />
            </div>
          </div>
          <div className='mt-3'>
            <MobileNumberField
              register={register}
              id='mobile'
              error={
                errors?.mobile ||
                (mobileValidationError
                  ? { message: mobileValidationError }
                  : undefined)
              }
              autoFocus={false}
              onChange={e => {
                const value = e.target.value;
                if (value.length <= 10) {
                  register('mobile').onChange(e);
                }
              }}
            />
          </div>
        </div>
        <div aria-label='address'>
          <Heading className='px-0 text-left text-sm' as='h4'>
            Address
          </Heading>
          <div className='mt-4 space-y-3'>
            <div className='relative'>
              <AddressField
                ref={register('street1').ref}
                placeholder='Address Line 1*'
                className='border border-black/20 bg-white'
                defaultValue={address ? address.street[0] : ''}
                isAddressForm={true}
                isCustomerAddressForm={true}
                onServiceabilityChange={isServiceable => {
                  setIsLocationServiceable(isServiceable);
                }}
                onAddressSelect={data => {
                  setValue('street1', data.address);

                  // Set city if available
                  if (data.city) {
                    setValue('city', data.city);
                    trigger('city');
                  }

                  // Set state if available - match with available regions
                  if (data.state && availableRegions?.length > 0) {
                    // Clean up the incoming state name
                    const cleanStateSearch = data.state
                      ?.toLowerCase()
                      .replace(/\s*state\s*/i, '')
                      .trim();

                    // First try exact match with common variations
                    let matchedRegion = availableRegions.find(region => {
                      const regionName = region.name.toLowerCase();
                      const regionCode = region.code.toLowerCase();
                      return (
                        regionName === cleanStateSearch ||
                        regionCode === cleanStateSearch ||
                        `${regionName} state` === cleanStateSearch
                      );
                    });

                    // If no exact match, try partial match with state codes and names
                    if (!matchedRegion) {
                      matchedRegion = availableRegions.find(region => {
                        const regionName = region.name.toLowerCase();
                        const regionCode = region.code.toLowerCase();

                        // Check if state name contains region name or code
                        return (
                          cleanStateSearch.includes(regionName) ||
                          cleanStateSearch.includes(regionCode) ||
                          // Common state name variations
                          (regionName === 'delhi' &&
                            cleanStateSearch.includes('new delhi')) ||
                          (regionName === 'telangana' &&
                            cleanStateSearch.includes('hyderabad')) ||
                          (regionName === 'uttar pradesh' &&
                            cleanStateSearch.includes('up'))
                        );
                      });
                    }

                    // If we found a matching region, set it; otherwise clear the selection
                    if (matchedRegion) {
                      setSelectedRegion(matchedRegion);
                      setValue('regionid', Number(matchedRegion.id));
                    } else {
                      setSelectedRegion(undefined);
                      setValue('regionid', null);
                    }
                    trigger('regionid');
                  }

                  // Set coordinates if available
                  if (data.latitude) setValue('latitude', data.latitude);
                  if (data.longitude) setValue('longitude', data.longitude);
                }}
              />
            </div>
            <TextField
              id='street2'
              placeholder='Enter your full address'
              register={register}
              error={errors?.street2}
            />
            <TextField
              id='pincode'
              placeholder='Pincode*'
              register={register}
              error={errors?.pincode}
              type='number'
            />
            <TextField
              id='city'
              placeholder='City*'
              register={register}
              error={errors?.city}
              defaultValue={!address ? defaultCity : address.city}
            />
            {availableRegions && availableRegions.length > 0 && (
              <DropDownList
                key={selectedRegion?.id}
                id='regionid'
                placeholder='State'
                options={availableRegions}
                setValue={(id: 'regionid', e: State) => {
                  setSelectedRegion(e);
                  setValue(id, Number(e.id));
                  trigger('regionid');
                }}
                defaultSelectedValue={selectedRegion}
                error={errors.regionid}
                trigger={trigger}
              />
            )}
          </div>

          <label className='mt-4 flex cursor-pointer gap-x-2 text-sm'>
            <CheckBox id='default_shipping' register={register} />
            This address is my default address
          </label>
        </div>
        <Button
          type='submit'
          label='Save Address'
          disabled={
            isSubmitting ||
            !isLocationServiceable ||
            !watch('firstname') ||
            !watch('lastname') ||
            !watch('mobile') ||
            !watch('street1') ||
            !watch('city') ||
            !watch('regionid')
          }
        />
      </form>
    </>
  );
};

export default CustomerAddressForm;
